import { isFunction } from 'lodash';
import { Check } from 'lucide-react';
import { tv } from 'tailwind-variants';

import { Badge } from '@/components/badge';
import { ButtonProps } from '@/components/button';
import { Heading } from '@/components/heading';
import { Link } from '@/components/link';
import { SubscribeButton } from '@/components/modules/subscriptions/subscribe-button';
import { Text } from '@/components/text';
import { marketingPages } from '@/lib/constants/marketing-site';
import { SubscriptionPlan, SubscriptionPrice } from '@/types';
import {
  checkIsEnterprisePlan,
  checkIsFreePlan,
  getPlanIntervalString,
  getPlanPrice,
  getPlanPriceString,
} from '@/utils/subscriptions';

import { PlanItemInclusions } from './inclusions';

export interface PlanItemParams {
  plan: SubscriptionPlan;
  isSelected?: boolean;
  buttonText?: string;
  buttonVariant?: ButtonProps['variant'];
  promoText?: string;
}

export interface PlanItemProps extends PlanItemParams {
  showAnnualPrice?: boolean;
  isSelectable?: boolean;
  descriptionText?: string | React.ReactNode;
  onSelect?: (price: SubscriptionPrice) => void;
}

const PlanItem = ({
  plan,
  showAnnualPrice = false,
  isSelected = false,
  isSelectable = false,
  buttonText = 'Sign up now',
  buttonVariant = 'primaryLight',
  promoText = '',
  descriptionText,
  onSelect,
}: PlanItemProps) => {
  const planPrice = getPlanPrice(plan, showAnnualPrice);
  const isFreePlan = checkIsFreePlan(plan);
  const isEnterprisePlan = checkIsEnterprisePlan(plan);
  const badgeText = plan.metadata?.badge || '';

  const handleSelectPlan = () => {
    if (isSelectable && isFunction(onSelect) && planPrice) {
      onSelect(planPrice);
    }
  };

  const {
    base,
    content,
    priceCard,
    headingWrapper,
    heading,
    description,
    planPriceHeading,
    planPriceSubText,
    annualPrice,
    currencyText,
    selectButton,
  } = planItemStyles({
    isClickable: isSelectable,
    isSelected,
  });

  return (
    <div
      tabIndex={isSelectable ? 0 : undefined}
      role="button"
      className={base()}
      onClick={() => handleSelectPlan()}
      onKeyUp={({ key }) => [' ', 'Enter'].includes(key) && handleSelectPlan()}
    >
      <div className={content()}>
        <div className="w-full max-w-[650px]">
          <header>
            <div className={headingWrapper()}>
              <Heading variant="h3" as="h3" className={heading()}>
                {plan.name}
              </Heading>
              {badgeText && (
                <Badge variant="positiveLight" size="md">
                  {badgeText}
                </Badge>
              )}
            </div>
            <Text variant="secondary" className={description()}>
              {descriptionText || plan.description}
            </Text>
          </header>
          {showAnnualPrice && !isFreePlan && <span className={annualPrice()}>15% off</span>}
          <PlanItemInclusions entitlements={plan.entitlements} />
        </div>
        <div className={priceCard()}>
          {planPrice && (
            <div className="flex flex-col">
              <Heading as="h4" className={planPriceHeading()} key={planPrice.id}>
                {getPlanPriceString(planPrice, showAnnualPrice)}
                {!isFreePlan && <small className={currencyText()}>{planPrice.currency}</small>}
              </Heading>
              <Text className="mb-0 text-sm uppercase">{getPlanIntervalString(planPrice, showAnnualPrice)}</Text>
            </div>
          )}

          <div className="mt-6">
            {!isSelectable ? (
              <SubscribeButton
                variant={buttonVariant}
                plan={plan}
                buttonText={buttonText}
                buttonDisabled={isSelected}
                showArrow={!isSelected}
                annualPriceSelected={showAnnualPrice}
              />
            ) : (
              <span className={selectButton()}>
                {isSelected && <Check className="size-4" />}
                {isSelected ? 'Selected' : 'Choose plan'}
              </span>
            )}
            {isEnterprisePlan ? (
              <Text className={planPriceSubText()}>
                Looking for a custom plan or payment option? <Link href={marketingPages.contact.url}>Contact us</Link>
              </Text>
            ) : (
              <Text className={planPriceSubText()}>{promoText}</Text>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const planItemStyles = tv({
  slots: {
    base: 'relative cursor-default rounded-lg border border-grey-200 bg-white px-6 py-5 shadow-soft md:px-8 md:py-6',
    content: 'md:flex md:justify-between md:gap-8',
    priceCard:
      '-mx-4 -mb-2 mt-6 flex min-w-[315px] flex-col justify-between rounded-lg bg-grey-200 p-6 md:-my-1 md:-mr-3 md:ml-0',
    headingWrapper: 'mb-1 gap-2 md:flex md:items-end',
    heading: 'm-0 text-lg font-semibold leading-none',
    description: 'mb-0 text-baseSm font-normal leading-normal text-grey-700',
    planPriceHeading: 'mb-1 text-lg font-semibold leading-ultraTight',
    planPriceSubText: 'mb-0 mt-3 text-xs italic opacity-70 [&_a]:underline',
    annualPrice:
      'absolute right-4 top-4 inline-block rounded-md bg-yellow px-2 py-1 text-sm font-semiBold leading-snug',
    currencyText: 'ml-1 text-baseSm uppercase',
    selectButton:
      'inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-lightBlue-500 px-4 py-3 text-baseSm font-medium leading-none text-white hover:bg-lightBlue-700',
  },
  variants: {
    isSelected: {
      true: {
        base: 'border-lightBlue-500',
        priceCard: 'bg-lightBlue-600 text-white',
        selectButton: 'bg-blue-600',
      },
    },
    isClickable: {
      true: {
        base: 'cursor-pointer hover:border-lightBlue-500 hover:outline hover:outline-1 hover:outline-lightBlue-500',
      },
    },
  },
});

export { PlanItem };
